/**
 * Build benefits worksheet from AG Grid API
 *
 * Extracts column definitions and row data from a benefits grid and
 * creates an ExcelJS worksheet with the same structure.
 */

import { Worksheet } from "exceljs";
import { GridApi, ValueFormatterParams } from "ag-grid-community";
import { logger } from "@platform/browser-logger";

import { exportValueFormatters } from "./exportFormatters";
import {
  addGroupedHeaderRows,
  buildGroupedHeaderLevels,
  detectNetworkMergeRanges,
  hasColumnGroupHierarchy,
} from "./networkHeaderHelpers";

import type { CellValue, ExportPlanLayout, PlanColumnSpans } from "../types";

export { fetchCoveragesData } from "./fetchCoveragesData";

// Benefit rows the analyst enters in the Build grid but that must not appear in
// the RFP template export (PB-698). rateGuaranteeCap is dental-only; the rest
// exist on every coverage, so a Set keyed by benefitKey covers all sheets.
const exportExcludedBenefitKeys = new Set<string>([
  "participationRequirements",
  "rateGuaranteeType",
  "rateGuaranteeAmount",
  "rateGuaranteeCap",
]);

const buildExportPlanLayout = (
  columns: ReturnType<GridApi["getColumns"]>,
): ExportPlanLayout => {
  const leafColumns = (columns ?? []).slice(1);
  const availablePlanIds = Array.from(
    new Set(
      leafColumns
        .map((column) => column.getColDef().field)
        .filter((field): field is string => Boolean(field))
        .map((field) => field.split(".")[0]),
    ),
  );

  const planIdsInOrder = leafColumns.reduce<string[]>((planIds, column) => {
    const field = column.getColDef().field;
    if (!field) {
      return planIds;
    }

    const planId = field.split(".")[0];
    if (!availablePlanIds.includes(planId) || planIds.includes(planId)) {
      return planIds;
    }

    planIds.push(planId);
    return planIds;
  }, []);

  const planColumnSpans = planIdsInOrder.reduce<PlanColumnSpans>(
    (spans, planId) => {
      spans[planId] = leafColumns.filter((column) => {
        const field = column.getColDef().field;
        return field === planId || field?.startsWith(`${planId}.`);
      }).length;
      return spans;
    },
    {},
  );

  return {
    planIdsInOrder,
    planColumnSpans,
  };
};

/**
 * Build a benefits worksheet from an AG Grid API
 *
 * @param worksheet - ExcelJS worksheet to populate
 * @param gridApi - AG Grid API instance from HiddenPlanGrid
 */
export function buildBenefitsWorksheet(
  worksheet: Worksheet,
  gridApi: GridApi,
  coverageType: string,
): ExportPlanLayout {
  // Get column definitions
  const columns = gridApi.getColumns();
  if (!columns || columns.length === 0) {
    throw new Error("No columns found in grid");
  }

  // ------------------------------------------------------------------
  // Build header rows
  // ------------------------------------------------------------------
  const isGrouped = hasColumnGroupHierarchy(columns);

  if (isGrouped) {
    // Multi-level headers: emit one row per depth level with merged cells
    const headerLevels = buildGroupedHeaderLevels(columns);
    addGroupedHeaderRows(worksheet, headerLevels, columns.length);
  } else {
    // Flat headers: single row of leaf column names (existing behaviour)
    const headerRow = columns.map((col) => col.getColDef().headerName || "");
    const excelHeaderRow = worksheet.addRow(headerRow);
    excelHeaderRow.font = { bold: true };
    excelHeaderRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
      cell.alignment = {
        horizontal: colNumber === 1 ? "left" : "center",
        vertical: "middle",
      };
    });
  }

  // Detect network merge ranges for network-agnostic data rows
  const networkMergeRanges = isGrouped ? detectNetworkMergeRanges(columns) : [];

  // Look up export formatter overrides for this coverage type
  const coverageFormatters = exportValueFormatters[coverageType];

  // Track the previous row's resolved values per column so that
  // conditional formatters can inspect the "type" row above.
  let previousRowValues: Record<string, unknown> = {};

  // Extract and add data rows
  gridApi.forEachNode((node) => {
    if (node.data) {
      const data = node.data as Record<string, unknown>;

      // Participation Requirements and the Rate Guarantee / Rate Caps block are
      // authored in the Build grid but excluded from the RFP template export
      // (PB-698). Keep them out of the spreadsheet without hiding them in the UI.
      if (exportExcludedBenefitKeys.has(data["benefitKey"] as string)) {
        return;
      }

      const benefit = data["benefit"] as Record<string, unknown> | undefined;
      // Check if this row is a child/nested row
      const isChildRow =
        benefit?.["nested"] === true || data["propertyKey"] !== undefined;
      const isParentRow = !isChildRow;
      const isNetworkDependent = benefit?.["networkDependent"] === true;

      // Build row data by mapping over columns
      const rowData: (string | number)[] = [];
      const benefitKey = data["benefitKey"] as string | undefined;
      const currentRowValues: Record<string, unknown> = {};

      columns.forEach((col) => {
        const colDef = col.getColDef();
        const field = colDef.field;

        // Get value using valueGetter if available, otherwise direct field access
        let value: CellValue;
        if (colDef.valueGetter && typeof colDef.valueGetter === "function") {
          value = colDef.valueGetter({
            data,
            node,
            colDef,
            column: col,
            api: gridApi,
            context: undefined,
            getValue: (field: string) => data[field],
          }) as CellValue;
        } else if (field && field.includes(".")) {
          const parts = field.split(".");
          value = parts.reduce<unknown>((obj, key) => {
            if (obj && typeof obj === "object" && key in obj) {
              return (obj as Record<string, unknown>)[key];
            }
            return undefined;
          }, data) as CellValue;
        } else if (field) {
          value = data[field] as CellValue;
        } else {
          value = "";
        }

        // Check for export formatter override before falling through to
        // AgGrid's valueFormatter. This handles complex values (arrays of
        // objects) that AgGrid cannot serialise to a meaningful string.
        if (coverageFormatters && benefitKey && field) {
          const entry = coverageFormatters[benefitKey];
          if (entry) {
            const previousValue = previousRowValues[field];
            const shouldFormat =
              !entry.condition || entry.condition(previousValue);
            if (shouldFormat) {
              currentRowValues[field] = value;
              rowData.push(entry.formatter(value));
              return;
            }
          }
        }

        // Apply valueFormatter if defined (for complex types like heaped commissions)
        if (
          colDef.valueFormatter &&
          typeof colDef.valueFormatter === "function"
        ) {
          try {
            const formatted = colDef.valueFormatter({
              value,
              data,
              node,
              colDef,
              column: col,
              api: gridApi,
            } as ValueFormatterParams);
            rowData.push(formatted ?? "");
            return;
          } catch (error) {
            logger.warn(`Error applying valueFormatter for field ${field}`, {
              error,
            });
          }
        }

        rowData.push(value ?? "");
      });

      // Update previous row values for the next iteration's condition checks
      // For columns not already captured by a formatter, store the raw value.
      columns.forEach((col) => {
        const field = col.getColDef().field;
        if (field && !(field in currentRowValues)) {
          let raw: unknown;
          const colDef = col.getColDef();
          if (colDef.valueGetter && typeof colDef.valueGetter === "function") {
            raw = colDef.valueGetter({
              data,
              node,
              colDef,
              column: col,
              api: gridApi,
              context: undefined,
              getValue: (f: string) => data[f],
            });
          } else {
            raw = data[field];
          }
          currentRowValues[field] = raw;
        }
      });
      previousRowValues = currentRowValues;

      const addedRow = worksheet.addRow(rowData);

      // Merge network sub-columns for network-agnostic benefit rows
      if (!isNetworkDependent && networkMergeRanges.length > 0) {
        networkMergeRanges.forEach(([startCol, endCol]) => {
          worksheet.mergeCells(
            addedRow.number,
            startCol,
            addedRow.number,
            endCol,
          );
        });
      }

      // Apply parent row styling — only bold column A, not plan value cells
      if (isParentRow) {
        addedRow.getCell(1).font = { bold: true };
      }

      // Apply alignment, borders, and conditional gray fill to data cells
      addedRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
        if (colNumber === 1) {
          cell.alignment = { horizontal: "left", vertical: "middle" };
        } else {
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.border = {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          };
          if (isParentRow) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFD3D3D3" },
            };
          }
        }
      });
    }
  });

  // Auto-size columns
  worksheet.columns.forEach((column) => {
    let maxLength = 10;
    column.eachCell?.({ includeEmpty: true }, (cell) => {
      const cellValue = cell.value?.toString() || "";
      if (cellValue.length > maxLength) {
        maxLength = cellValue.length;
      }
    });
    column.width = Math.min(maxLength + 2, 50);
  });

  return buildExportPlanLayout(columns);
}
